import type { FacilityHit, GlobalHit, PersonHit } from "@/components/AlgoliaSearch";
import type { MentionExpandContext, MentionUser } from "@/components/AtMention/types";
import { splitMentionName } from "@/components/AtMention/mentionHelpers";
import { TYPENAME_TO_REFERENCEABLE_TYPE } from "@/features/global/components/NotesTable/mutations";
import {
  getReferenceIcon,
  getReferenceSupportingText,
  getReferenceTypeLabel
} from "@/features/global/components/ReferencesTable/types";
import type { EventSearchNode } from "@/pages/tasks/[taskId]/_hooks/eventsForReferenceSearchQuery";
import type {
  ReferenceLookupContext,
  ReferencePickerItem
} from "@/pages/tasks/[taskId]/_hooks/useReferencePickerItems";

export const QUICK_ADD_MENTION_OVERLAY = {
  prompt: "Mention a teammate or attach a person, facility, event, or note",
  empty: "No matches found",
  ariaLabel: "Mention a teammate or attach a record"
};

export const QUICK_ADD_EXPAND_OVERLAY = {
  prompt: "Select this record or a related note",
  empty: "No related records found",
  ariaLabel: "Select a record to attach"
};

export const BACK_MENTION_ID = "mention-back";

export const BACK_MENTION_USER: MentionUser = {
  id: BACK_MENTION_ID,
  firstName: "Back to search",
  lastName: "",
  email: "",
  kind: "back",
  iconName: "arrow_back",
  selectAction: "back"
};

function isPersonHit(hit: GlobalHit): hit is PersonHit {
  return (
    hit.object_type === "Person" &&
    (("pga_id" in hit && typeof hit.pga_id === "string") || ("legacy_id" in hit && typeof hit.legacy_id === "string"))
  );
}

function isFacilityHit(hit: GlobalHit): hit is FacilityHit {
  return hit.object_type === "Facility" && typeof hit.facility_id === "string";
}

export function teammateMention(user: MentionUser): MentionUser {
  return { ...user, kind: user.kind ?? "teammate", selectAction: "insert" };
}

export function mapAlgoliaHitToMention(hit: GlobalHit): MentionUser | null {
  if (isPersonHit(hit)) {
    const pgaId = hit.pga_id || hit.legacy_id;
    if (!pgaId) return null;
    const { firstName, lastName } = splitMentionName(hit.preferred_name || hit.name);
    return {
      id: `expand:Person:${pgaId}`,
      firstName,
      lastName,
      email: hit.email || "",
      photo: hit.profile_photo,
      kind: "person",
      subtitle: [hit.section_name, hit.facility_name].filter(Boolean).join(" · ") || undefined,
      iconName: "person",
      selectAction: "expand",
      expandContext: { type: "Person", id: pgaId },
      sectionName: hit.section_name,
      state: hit.state
    };
  }

  if (isFacilityHit(hit)) {
    const { firstName, lastName } = splitMentionName(hit.facility_name || hit.name);
    const location = [hit.city, hit.state].filter(Boolean).join(", ");
    return {
      id: `expand:Facility:${hit.facility_id}`,
      firstName,
      lastName,
      email: "",
      kind: "facility",
      subtitle: location || hit.section_name || undefined,
      iconName: "location_on",
      selectAction: "expand",
      expandContext: {
        type: "Facility",
        id: hit.facility_id,
        ...(hit.legacy_id ? { legacyId: hit.legacy_id } : {}),
        name: hit.facility_name || hit.name,
        city: hit.city,
        state: hit.state
      },
      sectionName: hit.section_name,
      state: hit.state
    };
  }

  return null;
}

export function mapEventToMention(event: EventSearchNode): MentionUser {
  const { firstName, lastName } = splitMentionName(event.title);
  const subtitle = [event.facility?.name, event.city, event.state].filter(Boolean).join(" · ") || undefined;
  return {
    id: `expand:Event:${event.id}`,
    firstName,
    lastName,
    email: "",
    kind: "event",
    subtitle,
    iconName: "event",
    selectAction: "expand",
    expandContext: {
      type: "Event",
      id: event.id,
      slug: event.slug,
      title: event.title,
      status: event.status,
      startsAt: event.startsAt
    },
    state: event.state
  };
}

export function mapPickerItemToMention(item: ReferencePickerItem): MentionUser | null {
  const referenceableType = TYPENAME_TO_REFERENCEABLE_TYPE[item.__typename];
  if (!referenceableType) return null;
  const { firstName, lastName } = splitMentionName(item.displayName);
  const supporting = getReferenceSupportingText(item);
  const subtitle = supporting.split("\n")[0] || getReferenceTypeLabel(item.__typename, item.notableType);
  const kind =
    item.__typename === "CRMPerson"
      ? "person"
      : item.__typename === "CRMTaskFacility"
        ? "facility"
        : item.__typename === "CRMEvent"
          ? "event"
          : "reference";
  return {
    id: `${referenceableType}:${item.id}`,
    firstName,
    lastName,
    email: "",
    kind,
    subtitle,
    iconName: getReferenceIcon(item.__typename),
    selectAction: "insert",
    sectionName: undefined,
    state: item.facilityState
  };
}

export function toLookupContext(context: MentionExpandContext): ReferenceLookupContext {
  return context;
}
